import { useCallback, useState, useEffect } from "react";

import { Icon, StreamingIcons } from "@/components/atoms/Icon";
import { Slider } from "@/components/atoms/Slider";

import { BrightnessSliderProps } from "./types";

/**
 * BrightnessSlider component for controlling video brightness
 * Displays a horizontal slider with brightness icon and draggable thumb
 *
 * Features:
 * - Visual brightness control using CSS filters on video element
 * - Persistent brightness settings (localStorage)
 * - Platform-aware styling (TV, Web, Mobile)
 * - Touch and mouse support
 *
 * @param videoRef - Reference to the video element to apply brightness filter
 * @param ariaLabel - Optional aria label override
 * @param disabled - Optional disabled state
 * @param onDragStart - Optional callback when dragging starts
 * @param onDragEnd - Optional callback when dragging ends
 * @param className - Additional CSS class for styling
 *
 * @returns The BrightnessSlider component
 *
 * @example
 * ```tsx
 * <BrightnessSlider videoRef={videoRef} />
 * ```
 */
const BrightnessSlider = ({
  videoRef,
  ariaLabel,
  disabled = false,
  onDragStart,
  onDragEnd,
  className,
}: BrightnessSliderProps) => {
  const [brightness, setBrightness] = useState<number>(100);

  /**
   * Apply brightness filter to video element
   */
  const applyBrightness = useCallback(
    (value: number) => {
      if (videoRef?.current) {
        const filterValue = value / 100;
        videoRef.current.style.filter = `brightness(${filterValue})`;
      }
    },
    [videoRef],
  );

  /**
   * Initialize brightness from localStorage
   */
  useEffect(() => {
    try {
      const savedBrightness = localStorage.getItem("videoBrightness");
      if (savedBrightness) {
        const value = parseInt(savedBrightness, 10);
        if (!isNaN(value) && value >= 0 && value <= 100) {
          setBrightness(value);
          applyBrightness(value);
        }
      }
    } catch (error) {
      /** localStorage might be unavailable (private browsing, storage full, etc.) */
      console.warn("Failed to read brightness from localStorage:", error);
    }
  }, [applyBrightness]);

  /**
   * Cleanup: Reset brightness filter on unmount
   * Capture the video element reference to ensure we clean up the correct element
   */
  useEffect(() => {
    const videoElement = videoRef?.current;
    return () => {
      if (videoElement) {
        videoElement.style.filter = "";
      }
    };
  }, [videoRef]);

  /**
   * Handle brightness change
   */
  const handleBrightnessChange = useCallback(
    (newValue: number) => {
      setBrightness(newValue);
      applyBrightness(newValue);
      try {
        localStorage.setItem("videoBrightness", newValue.toString());
      } catch (error) {
        /** localStorage might be unavailable (private browsing, storage full, etc.) */
        console.warn("Failed to save brightness to localStorage:", error);
      }
    },
    [applyBrightness],
  );

  return (
    <Slider
      value={brightness}
      onChange={handleBrightnessChange}
      icon={<Icon icon={StreamingIcons.Settings} />}
      ariaLabel={ariaLabel || "Brightness"}
      disabled={disabled}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={className}
    />
  );
};

export { BrightnessSlider };
